import { component } from 'picoapp';
import Swiper, { Navigation, Pagination } from 'swiper';
import Drawer from '../../../js/utils/drawer';

export default component(async (node, ctx) => {
  /**
   * Data + elements
   */
  const modelToggles = node.querySelectorAll(' .js-modelToggle');
  const productItems = node.querySelectorAll(' .js-productItem');
  const filterDrawer = node.querySelector(' .js-filterDrawer');
  const filterDrawerBtns = node.querySelectorAll('.js-filterDrawerBtn');
  const infoDrawers = node.querySelectorAll('.js-infoDrawer');
  const infoDrawerTriggers = node.querySelectorAll('.js-infoDrawerTrigger');
  const filterBarScroll = node.querySelector('.js-filterBarScroll');
  const filterBarStatic = node.querySelector('.js-filterBarStatic');
  const filters = node.querySelectorAll('.js-filter');
  const collectionItems = node.querySelectorAll('.js-collectionItem');
  const productListItems = node.querySelectorAll('.js-productListItem');
  const filterLists = node.querySelectorAll('.js-filterList');
  const clearFilterBtns = node.querySelectorAll('.js-clearFilterBtn');
  const resultCount = node.querySelector('.js-resultCount');
  const filterCounts = node.querySelectorAll('.js-filterCount');
  const filterDesktop = document.querySelector('.js-filterDesktop');
  const header = document.querySelector(".header");

  const sliderElement = node.querySelector('.collections__info-drawer .js-slider');
  const sliderPagination = node.querySelector('.collections__info-drawer .js-sliderPagination');
  const mediaSliderPrevBtn = node.querySelector('.collections__info-drawer .js-mediaSliderPrevBtn');
  const mediaSliderNextBtn = node.querySelector('.collections__info-drawer .js-mediaSliderNextBtn');
  const swiperWrapper = node.querySelector('.collections__info-drawer .swiper');

  const filterBarActiveClass = 'collections__filter-bar--active';
  const filterBarHeaderActiveClass = 'collections__filter-bar--header-active';
  const modelToggleActiveClass = 'collections__model-toggle--active';
  const productItemToggledClass = 'product-item--toggled';

  const strings = window.theme.strings.collection;

  let previousScrollPos = window.scrollY;
  let slider;
  let hydrateProductGalleryFn = (_item, _fullHydration = false) => { };

  /**
   * Functions
   */
  const initSlider = () => {
    // Fabric slider is only rendered when at least one block has a fabric image
    if (!sliderElement) return;

    const reset = () => {
      if (slider?.destroy) {
        slider.destroy();
      }

      slider = new Swiper(sliderElement, {
        modules: [Pagination, Navigation],
        effect: 'coverflow',
        pagination: {
          el: sliderPagination,
          type: 'bullets',
          bulletClass: 'image-slider__page-bullet',
          bulletActiveClass: 'image-slider__page-bullet--active',
        },
        navigation: {
          prevEl: mediaSliderPrevBtn,
          nextEl: mediaSliderNextBtn,
        },
        centeredSlides: true,
        slidesPerView: 'auto',
        speed: 1000,
        loop: true,
      });
    };

    reset();
  };

  const initDeferredMedia = () => {
    const hydrateImageNode = (img) => {
      if (!img || img.dataset.hydrated === 'true') return;

      img.srcset = img.dataset.deferredSrcset;
      img.src = img.dataset.deferredSrc;
      img.dataset.hydrated = 'true';

      if (img.complete) {
        img.classList.remove('opacity-0');
      } else {
        img.onload = () => img.classList.remove('opacity-0');
      }
    };

    const hydrateVideoNode = (video) => {
      if (!video || video.dataset.hydrated === 'true') return;

      video.muted = true;
      video.defaultMuted = true;
      video.src = video.dataset.src;
      video.load();
      video.dataset.hydrated = 'true';
    };

    const hydrateExistingSlideMedia = (item, fullHydration = false) => {
      const selector = fullHydration ? '.swiper-slide' : '.swiper-slide[data-index="1"]';
      item.querySelectorAll(selector).forEach((slide) => {
        slide.querySelectorAll('.js-deferredImage').forEach((img) => hydrateImageNode(img));
        slide.querySelectorAll('.js-deferredVideo').forEach((video) => hydrateVideoNode(video));
      });
    };

    const hydrateTemplateSlides = (item, fullHydration = false) => {
      const swiperWrapper = item.querySelector('.js-mediaSlider .swiper-wrapper');
      if (!swiperWrapper) return false;

      const templates = item.querySelectorAll('template.js-deferredSlide');
      if (templates.length === 0) return false;

      let hasHydratedContent = false;

      templates.forEach((template) => {
        const index = parseInt(template.dataset.index, 10);

        // If not full hydration, only load the second image (index 1) for hover flip
        if (!fullHydration && index > 1) return;

        const clone = template.content.cloneNode(true);
        const img = clone.querySelector('.js-deferredImage');
        const video = clone.querySelector('.js-deferredVideo');

        if (img) hydrateImageNode(img);
        if (video) hydrateVideoNode(video);

        swiperWrapper.appendChild(clone);
        template.remove();
        hasHydratedContent = true;
      });

      return hasHydratedContent;
    };

    // 1. Define the hydration logic once to avoid duplication
    const hydrateProductGallery = (item, fullHydration = false) => {
      hydrateExistingSlideMedia(item, fullHydration);
      const hydratedTemplateSlides = hydrateTemplateSlides(item, fullHydration);

      // Update Swiper instance
      const swiperInstance = item.querySelector('.js-mediaSlider')?.swiper;
      if (swiperInstance) {
        if (hydratedTemplateSlides) {
          swiperInstance.update();
        }

        // If we just loaded the second image, ensure swiper recognizes it
        if (!fullHydration) {
          // Add listener for when user actually starts interacting with the slider
          swiperInstance.on('beforeTransitionStart', () => {
            hydrateProductGallery(item, true);
          }, { once: true });
        }
      }
    };
    hydrateProductGalleryFn = hydrateProductGallery;

    // 2. Setup Intersection Observer for Mobile/Scroll
    const observerOptions = {
      root: null, // use the viewport
      rootMargin: '10px', // start loading 100px before it enters the screen
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const item = entry.target;
          hydrateProductGallery(item);
          observer.unobserve(item); // Stop watching this item once loaded
        }
      });
    }, observerOptions);

    // 3. Attach listeners to all product items
    productItems.forEach((item) => {
      // Desktop: Hover trigger -> Full Hydration
      item.addEventListener('mouseenter', () => {
        hydrateProductGallery(item, true);
        observer.unobserve(item); // Cleanup observer if mouse entered first
      }, { once: true });

      // Mobile: Scroll trigger -> Partial Hydration (just the hover image)
      observer.observe(item);
    });
  };

  const initSliderArrows = () => {
    if (document.querySelector('.swiper-initialized')) {
      swiperWrapper?.addEventListener('mouseover', function (el) {
        mediaSliderNextBtn.style.transform = 'translateX(0)';
        mediaSliderPrevBtn.style.transform = 'translateX(0)';
      });

      swiperWrapper?.addEventListener('mouseout', function (el) {
        mediaSliderNextBtn.style.transform = 'translateX(28px)';
        mediaSliderPrevBtn.style.transform = 'translateX(-28px)';
      });
    }
  };

  const initFilters = () => {
    const clearFilterList = () => {
      const filterValues = node.querySelectorAll('.js-filterValueBtn');
      filterValues.forEach((value) => value.remove());
    };

    const filterCollectionItems = (titles) => {
      if (titles.length) {
        collectionItems.forEach((item) => {
          const { title } = JSON.parse(item.dataset.properties);
          const products = item.querySelectorAll('.js-productItem');

          if (titles.includes(title)) {
            item.hidden = false;

            products.forEach((product) => {
              product.hidden = false;
            });
          } else {
            item.hidden = true;

            products.forEach((product) => {
              product.hidden = true;
            });
          }
        });

        titles.forEach((title) => {
          filterLists.forEach((filterList) => {
            const button = document.createElement('button');
            filterList.appendChild(button);

            button.outerHTML = `<button type="button" class="collections__filter-value js-filterValueBtn" data-target="collection" data-property="title" data-value="${title}">
              <span>${title}</span>
              ${window.theme.templates.icons.closeSmall}
            </button>`;
          });
        });
      } else {
        collectionItems.forEach((item) => {
          item.hidden = false;

          const products = item.querySelectorAll('.js-productItem');
          products.forEach((product) => {
            product.hidden = false;
          });
        });
      }
    };

    const filterProductItems = (properties) => {
      // Update filter counts
      filters.forEach((filter) => {
        const { property } = filter.dataset;
        const count = filter.querySelector('.js-filterItemCount');

        if (!count) return;

        if (properties[property]) {
          count.hidden = false;
          count.innerText = `(${properties[property].length})`;
        } else {
          count.hidden = true;
          count.innerText = `(${0})`;
        }
      });
      // Update products items visibility
      if (Object.keys(properties).length) {
        // Create buttons
        Object.keys(properties)
          .filter((key) => key !== 'title' && key !== 'available')
          .forEach((key) => {
            properties[key].forEach((value) => {
              filterLists.forEach((filterList) => {
                const button = document.createElement('button');
                filterList.appendChild(button);

                button.outerHTML = `<button type="button" class="collections__filter-value js-filterValueBtn" data-target="product" data-property="${key}" data-value="${value}">
                <span>${value}</span>
                ${window.theme.templates.icons.closeSmall}
              </button>`;
              });
            });
          });

        const activeFilterKeys = Object.keys(properties).filter(k => k !== 'available');
        const shouldFilterByAvailability = properties.available && properties.available.includes('true');

        productListItems.forEach((item) => {
          // If it's a promo block (no .js-productItem inside), don't hide it
          if (!item.querySelector('.js-productItem')) {
            item.hidden = false;
            return;
          }

          const rawData = JSON.parse(item.dataset.properties);
          const itemProps = Array.isArray(rawData) ? Object.assign({}, ...rawData) : rawData;
          const variants = itemProps.variants || [];

          // Product appears if at least one variant matches ALL filter categories AND is in stock (if availability filter is active)
          const hasMatchingVariant = variants.some((variant) => {
            // If availability filter is active, only show available variants
            if (shouldFilterByAvailability && !variant.available) return false;

            // For every active filter category (size, color, etc.)...
            return activeFilterKeys.every((key) => {
              let searchKey = key;
              if (key === 'product_type') searchKey = 'type';
              else if (key === 'brand') searchKey = 'vendor';
              else if (key === 'shirt_type') searchKey = 'model';

              const requiredValues = properties[key].map(v => v.toLowerCase());
              const variantTitle = variant.title.toLowerCase();
              const productMetaValue = String(itemProps[searchKey] || '').toLowerCase();

              // MATCH: If the variant matches ANY of the values selected for THIS category
              return requiredValues.some(val => {
                // For sizes/colors stored in variant titles
                const matchesVariant = variantTitle === val || variantTitle.split(' / ').includes(val);
                // For global product traits like vendor or type
                const matchesMeta = productMetaValue === val;

                return matchesVariant || matchesMeta;
              });
            });
          });

          item.hidden = !hasMatchingVariant;
        });
      } else {
        productListItems.forEach((item) => {
          item.hidden = false;
        });
      }
    };

    const initFilterValueBtns = () => {
      const btns = node.querySelectorAll('.js-filterValueBtn');

      btns.forEach((btn) => {
        const { target, property, value } = btn.dataset;
        const filter = Array.from(filters).find((filter) => filter.dataset.target === target && filter.dataset.property === property); // eslint-disable-line

        const matchingInput = Array.from(filter.querySelectorAll('.js-input')).find((input) => input.dataset.value === value);

        btn.addEventListener('click', () => {
          matchingInput.checked = false;
          matchingInput.dispatchEvent(new Event('change'));
        });
      });
    };

    const toggleFilterList = () => {
      filterLists.forEach((filterList) => {
        const filterValueBtns = filterList.querySelectorAll('.js-filterValueBtn');

        if (filterValueBtns.length) {
          filterList.hidden = false;
        } else {
          filterList.hidden = true;
        }
      });
    };

    const updateInfo = () => {
      const visibleProductItems = Array.from(productListItems).filter((item) => item.offsetParent !== null && item.querySelector('.js-productItem')); // eslint-disable-line

      resultCount.textContent = strings.viewResults.replace('%count%', visibleProductItems.length);
    };

    const getFilterProperties = () => {
      const collectionTitles = [];
      const productProperties = {};

      // Collect values
      filters.forEach((filter) => {
        const { target } = filter.dataset;
        const activeInputs = Array.from(filter.querySelectorAll('.js-input')).filter((input) => input.checked); // eslint-disable-line

        if (target === 'collection') {
          const titles = activeInputs.map((input) => input.dataset.value);

          if (titles.length) {
            collectionTitles.push(...titles);
          }
        } else if (target === 'product') {
          const { property } = filter.dataset;
          const values = activeInputs.map((input) => input.dataset.value);

          if (values.length) {
            productProperties[property] = values;
          }
        }
      });

      return { collectionTitles, productProperties };
    };

    const updateURLParams = () => {
      const { collectionTitles, productProperties } = getFilterProperties();
      const dataset = {
        ...productProperties,
      };

      if (collectionTitles.length) {
        dataset.title = collectionTitles;
      }

      const params = new URLSearchParams();

      Object.entries(dataset).forEach(([key, value]) => {
        params.set(key, value);
      });

      window.history.replaceState({}, '', `${window.location.pathname}${Object.keys(dataset).length ? `?${params.toString()}` : ''}`);
    };

    const readURLParams = () => {
      const params = new URLSearchParams(window.location.search);

      return Object.fromEntries(params);
    };

    const updateFilterStates = () => {
      const params = readURLParams();

      Object.values(params).forEach((values) => {
        values.split(',').forEach((value) => {
          const input = document.querySelector(`.js-input[data-value="${value}"]`);

          if (input) {
            input.checked = true;
          }
        });
      });
    };

    const updateValidFilterItems = () => {
      collectionItems.forEach((item) => {
        const listItems = item.querySelectorAll('.js-productListItem');
        const allHidden = Array.from(listItems).every((listItem) => listItem.hidden);

        if (allHidden) {
          item.hidden = true;
        }
      });

      filters.forEach((filter) => {
        if (filter.dataset.property === 'available') return;

        const listItems = filter.querySelectorAll('li');
        const allHidden = Array.from(listItems).every((listItem) => listItem.hidden);

        if (allHidden) {
          filter.hidden = true;
        } else {
          filter.hidden = false;
        }
      });
    };

    const runFilter = () => {
      const { collectionTitles, productProperties } = getFilterProperties();

      const totalCount = collectionTitles.length + Object.values(productProperties).length;
      filterCounts.forEach((filterCount) => {
        filterCount.innerText = `(${totalCount})`;

        if (totalCount > 0) {
          filterCount.hidden = false;
        } else {
          filterCount.hidden = true;
        }
      });

      const dataset = {
        ...productProperties,
      };

      if (collectionTitles.length) {
        dataset.title = collectionTitles;
      }

      clearFilterList();
      filterCollectionItems(collectionTitles);
      filterProductItems(dataset);
      updateValidFilterItems();
      initFilterValueBtns();
      toggleFilterList();
      updateInfo();
    };

    if (Object.values(readURLParams()).length) {
      updateFilterStates();
      runFilter();
    } else {
      runFilter();
      updateURLParams();
    }

    const clearFilter = () => {
      filters.forEach((filter) => {
        const inputs = filter.querySelectorAll('.js-input');

        inputs.forEach((input) => {
          // Skip 'availability' filter, must not be reset
          if (input.id === 'availability') return;

          input.checked = false;
        });
      });

      runFilter();
      updateURLParams();
    };

    filters.forEach((filter) => {
      const inputs = filter.querySelectorAll('.js-input');

      inputs.forEach((input) => {
        input.addEventListener('change', () => {
          runFilter();
          updateURLParams();
        });
      });
    });

    clearFilterBtns.forEach((btn) => {
      btn.addEventListener('click', clearFilter);
    });
  };

  const handleScroll = () => {
    let scrollingUp = false;
    const currentScrollPos = window.scrollY;
    const isDesktop = window.innerWidth >= 1024;

    if (currentScrollPos < previousScrollPos) {
      scrollingUp = true;
    }

    previousScrollPos = currentScrollPos;

    const scrolledPastFilterBarStatic = filterBarStatic.getBoundingClientRect().bottom < 0;

    if (scrolledPastFilterBarStatic) {
      filterBarScroll.classList.add(filterBarActiveClass);
    } else {
      filterBarScroll.classList.remove(filterBarActiveClass);
    }

    // Offset the scroll filter bar by the header height only while the header
    // is visible (scrolling up). When scrolling down the header hides, so the
    // bar sits flush to the top of the viewport to avoid an empty gap above it.
    if (scrolledPastFilterBarStatic && scrollingUp) {
      filterBarScroll.classList.add(filterBarHeaderActiveClass);
    } else {
      filterBarScroll.classList.remove(filterBarHeaderActiveClass);
    }


    if (header && filterDesktop) {
      const headerHeight = header.offsetHeight;
      const filterBarTop = filterDesktop.getBoundingClientRect().top;

      if (filterBarTop <= headerHeight && isDesktop) {
        filterDesktop.classList.add("sticky-filter");
      } else {
        filterDesktop.classList.remove("sticky-filter");
      }

      // Pull the sticky filter up to the top of the viewport while the header
      // is hidden (scrolling down) so it tracks the header instead of leaving
      // an empty strip above it. When scrolling up the header reappears and the
      // filter drops back below it.
      if (isDesktop && !scrollingUp) {
        filterDesktop.classList.add("filter-desktop--header-hidden");
      } else {
        filterDesktop.classList.remove("filter-desktop--header-hidden");
      }
    }
  };

  const syncCollectionShowOnModelClass = () => {
    if (modelToggles[0]?.classList.contains(modelToggleActiveClass)) {
      document.documentElement.classList.add('js-collectionShowOnModel');
    } else {
      document.documentElement.classList.remove('js-collectionShowOnModel');
    }
  };

  const initModelToggle = () => {
    const toggleShowModel = () => {
      modelToggles.forEach((toggle) => {
        toggle.classList.toggle(modelToggleActiveClass);
      });

      syncCollectionShowOnModelClass();

      productItems.forEach((item) => {
        const swiper = item.querySelector('.swiper')?.swiper;
        if (!swiper) return;
        hydrateProductGalleryFn(item, true);
        swiper.slideTo(0);
      });
    };

    modelToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => toggleShowModel());
    });

    syncCollectionShowOnModelClass();
  };

  const initFilterDrawer = () => {
    const closeBtns = filterDrawer.querySelectorAll('.js-closeBtn');
    const drawer = Drawer(filterDrawer);

    filterDrawerBtns.forEach((btn) => {
      btn.addEventListener('click', () => drawer.open());
    });

    closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => drawer.close());
    });
  };

  const initInfoDrawers = () => {
    infoDrawers.forEach((infoDrawer) => {
      const drawer = Drawer(infoDrawer);

      const closeBtn = infoDrawer.querySelector('.js-closeBtn');
      closeBtn.addEventListener('click', () => drawer.close());

      infoDrawerTriggers.forEach(function (infoDrawerTrigger) {
        infoDrawerTrigger.addEventListener('click', function () {
          const currIndex = this.getAttribute('data-index');
          const currId = this.getAttribute('data-id');

          slider?.slideToLoop(Number(currIndex), 0);

          document.querySelectorAll(`.drawer__info [block-index]`).forEach((el) => {
            el.setAttribute('hidden', '');
          });

          document.querySelectorAll(`[block-index="${currIndex}"]`).forEach((el) => {
            el.removeAttribute('hidden');
            if (el.classList.contains('fabric_description')) {
              el.removeAttribute('hidden');
            }
          });

          document.querySelectorAll(`[block-id="${currId}"]`).forEach((el) => {
            if (el.classList.contains('collections__info-drawer__fabric-item')) {
              el.classList.add('font-semibold');
            }
          });

          drawer.open();
        });
      });
    });
  };

  const destroyInfoDrawers = () => {
    var observer = new MutationObserver(function (mutations) {
      // wait until drawer closes
      setTimeout(() => {
        mutations.forEach(function (mutation) {
          if (!mutation.target.classList.contains('overlay--active')) {
            document.querySelectorAll('.collections__info-drawer .drawer__header p').forEach(function (el) {
              el.setAttribute('hidden', '');
            });
            document.querySelectorAll('p.fabric_description').forEach(function (el) {
              el.setAttribute('hidden', '');
            });
            document.querySelectorAll('.collections__info-drawer__fabric-item').forEach(function (el) {
              el.classList.remove('font-semibold');
            });
          }
        });
      }, 1000);
    });

    observer.observe(document.querySelector('.overlay'), {
      attributes: true,
      attributeFilter: ['class'],
    });
  };

  const init = () => {
    initFilters();
    initModelToggle();
    initFilterDrawer();
    initInfoDrawers();
    handleScroll();
    initSlider();
    initSliderArrows();
    initDeferredMedia();
    destroyInfoDrawers();

    ctx.on('window:scroll', handleScroll);
    document.addEventListener('DOMContentLoaded', handleScroll);
  };

  /**
   * Init
   */
  init();
});
